import React, { FC, useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type {
  CreateHandbookPageRequest,
  HandbookPage,
  HandbookPageOrigin,
  HandbookPageVersion,
  UpdateHandbookDraftRequest,
} from '@dreamai/types';
import type { FlatHandbookNode } from './handbookTree';
import { IMPORT_MODE_LABELS } from './handbookImports';
import HandbookRichTextEditor from './HandbookRichTextEditor';
import { assistantCopy } from './organisationAssistantCopy';
import { HandbookDocumentPull, PullSource } from './HandbookDocumentPull';
import type { HandbookDocument, HandbookPageFill } from './handbookContract';
import { fillSourceTitle } from './handbookSetup';

/** Prototype only: lets a page be written from an uploaded document */
interface DocumentPullProps {
  loadDocuments?: () => Promise<HandbookDocument[]>;
  pullContent?: (
    title: string,
    source: PullSource,
  ) => Promise<HandbookPageFill | null>;
}

interface FormValues {
  title: string;
  description: string;
  parentSlug: string;
  bodyMarkdown: string;
}

const EMPTY_FORM: FormValues = {
  title: '',
  description: '',
  parentSlug: '',
  bodyMarkdown: '',
};

function fromVersion(version: HandbookPageVersion): FormValues {
  return {
    title: version.title,
    description: version.description ?? '',
    parentSlug: version.parentSlug ?? '',
    bodyMarkdown: version.bodyMarkdown,
  };
}

/** Pulled content replaces the body; the description only when none was written yet */
function pulledValues(
  values: FormValues,
  fill: HandbookPageFill,
): Partial<FormValues> {
  return values.description.trim() || !fill.description
    ? { bodyMarkdown: fill.bodyMarkdown }
    : { bodyMarkdown: fill.bodyMarkdown, description: fill.description };
}

function sameForm(a: FormValues, b: FormValues): boolean {
  return (
    a.title === b.title &&
    a.description === b.description &&
    a.parentSlug === b.parentSlug &&
    a.bodyMarkdown === b.bodyMarkdown
  );
}

interface ParentSelectProps {
  value: string;
  options: FlatHandbookNode[];
  disabled: boolean;
  onChange: (parentSlug: string) => void;
}

const ParentSelect: FC<ParentSelectProps> = ({
  value,
  options,
  disabled,
  onChange,
}) => (
  <label className='organisation-field'>
    <span className='field-label'>Section</span>
    <select
      className='field-select'
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
    >
      <option value=''>No section</option>
      {options
        .filter((o) => o.depth === 0)
        .map((o) => (
          <option key={o.slug} value={o.slug}>
            {o.title}
          </option>
        ))}
    </select>
  </label>
);

interface BodyFieldProps {
  value: string;
  disabled: boolean;
  readOnly: boolean;
  onChange: (bodyMarkdown: string) => void;
}

const BODY_PLACEHOLDER = 'Holiday policy\n\nEveryone gets 25 days a year…';

/**
 * The page body, edited in the rich-text editor. A read-only body (the live
 * version next to a draft) renders the Markdown instead of an editor.
 */
const BodyField: FC<BodyFieldProps> = ({
  value,
  disabled,
  readOnly,
  onChange,
}) => (
  <div className='organisation-field handbook-body-field'>
    <span className='field-label' id='handbook-body-label'>
      Content
    </span>
    {readOnly ? (
      <div
        className='handbook-preview'
        data-testid='handbook-preview'
        aria-labelledby='handbook-body-label'
      >
        {value.trim() ? (
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{value}</ReactMarkdown>
        ) : (
          <p className='organisation-empty'>This version has no content.</p>
        )}
      </div>
    ) : (
      <HandbookRichTextEditor
        value={value}
        disabled={disabled}
        placeholder={BODY_PLACEHOLDER}
        labelledBy='handbook-body-label'
        onChange={onChange}
      />
    )}
  </div>
);

// ── Create ──────────────────────────────────────────────────────────────

interface HandbookCreatePageProps extends DocumentPullProps {
  parentOptions: FlatHandbookNode[];
  /** See OrganisationHandbook: name Reve or the members' own assistants. */
  teamAssistantsAvailable: boolean;
  busy: boolean;
  initialParentSlug?: string | null;
  onCreate: (body: CreateHandbookPageRequest) => void;
  onCancel: () => void;
}

export const HandbookCreatePage: FC<HandbookCreatePageProps> = ({
  parentOptions,
  teamAssistantsAvailable,
  busy,
  initialParentSlug,
  onCreate,
  onCancel,
  loadDocuments,
  pullContent,
}) => {
  const [contentMode, setContentMode] = useState<'write' | 'pull'>('write');
  const [pulledFrom, setPulledFrom] = useState<string | null>(null);
  const canPull = !!loadDocuments && !!pullContent;
  const [values, setValues] = useState<FormValues>({
    ...EMPTY_FORM,
    parentSlug: initialParentSlug ?? '',
  });
  const update = (patch: Partial<FormValues>) =>
    setValues((v) => ({ ...v, ...patch }));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const title = values.title.trim();
    if (!title) return;
    const description = values.description.trim();
    onCreate({
      title,
      bodyMarkdown: values.bodyMarkdown,
      ...(description ? { description } : {}),
      parentSlug: values.parentSlug || null,
    });
  };

  return (
    <form
      className='organisation-card handbook-editor'
      data-testid='handbook-create'
      onSubmit={handleSubmit}
    >
      <div className='handbook-editor-header'>
        <h3 className='organisation-card-title'>New page</h3>
        <span className='handbook-badge draft'>Draft</span>
      </div>
      <p className='organisation-card-text'>
        The page is saved as a draft; publish it when it is ready for{' '}
        {assistantCopy(teamAssistantsAvailable).who} to answer from.
      </p>
      <label className='organisation-field'>
        <span className='field-label'>Title</span>
        <input
          type='text'
          className='field-input'
          value={values.title}
          maxLength={200}
          disabled={busy}
          placeholder='Holiday policy'
          onChange={(e) => update({ title: e.target.value })}
        />
      </label>
      <label className='organisation-field'>
        <span className='field-label'>Description (optional)</span>
        <input
          type='text'
          className='field-input'
          value={values.description}
          maxLength={500}
          disabled={busy}
          placeholder='How much leave people get and how to book it.'
          onChange={(e) => update({ description: e.target.value })}
        />
      </label>
      <div className='handbook-field-row'>
        <ParentSelect
          value={values.parentSlug}
          options={parentOptions}
          disabled={busy}
          onChange={(parentSlug) => update({ parentSlug })}
        />
      </div>
      {canPull && (
        <div className='organisation-field'>
          <span className='field-label'>How do you want to add content?</span>
          <div
            className='handbook-segmented handbook-content-mode'
            role='group'
            aria-label='How to add content'
          >
            <button
              type='button'
              className={`handbook-segment${contentMode === 'write' ? ' active' : ''}`}
              aria-pressed={contentMode === 'write'}
              onClick={() => setContentMode('write')}
            >
              Write it myself
            </button>
            <button
              type='button'
              className={`handbook-segment${contentMode === 'pull' ? ' active' : ''}`}
              aria-pressed={contentMode === 'pull'}
              onClick={() => setContentMode('pull')}
            >
              Pull content from a document
            </button>
          </div>
        </div>
      )}
      {contentMode === 'pull' && loadDocuments && pullContent ? (
        <HandbookDocumentPull
          title={values.title}
          disabled={busy}
          loadDocuments={loadDocuments}
          pull={pullContent}
          onPulled={(fill) => {
            update(pulledValues(values, fill));
            setPulledFrom(fillSourceTitle(fill));
            setContentMode('write');
          }}
        />
      ) : (
        <>
          {pulledFrom && (
            <p className='handbook-pulled'>
              Written from {pulledFrom}. Check it and change anything you need
              before you create the draft.
            </p>
          )}
          <BodyField
            key={pulledFrom ?? 'body'}
            value={values.bodyMarkdown}
            disabled={busy}
            readOnly={false}
            onChange={(bodyMarkdown) => update({ bodyMarkdown })}
          />
        </>
      )}
      <div className='handbook-editor-actions'>
        <button
          type='button'
          className='handbook-pill small'
          disabled={busy}
          onClick={onCancel}
        >
          Cancel
        </button>
        <div className='handbook-editor-actions-end'>
          <button
            type='submit'
            className='handbook-pill primary small'
            disabled={busy || !values.title.trim()}
          >
            {busy ? 'Creating…' : 'Create draft'}
          </button>
        </div>
      </div>
    </form>
  );
};

// ── Provenance ──────────────────────────────────────────────────────────

interface HandbookOriginBlockProps {
  origin: HandbookPageOrigin;
}

/** Where an imported version came from, with the compiler's fidelity check. */
const HandbookOriginBlock: FC<HandbookOriginBlockProps> = ({ origin }) => {
  const fidelity = origin.fidelity;
  return (
    <div className='handbook-origin' data-testid='handbook-origin'>
      <div className='handbook-header-row'>
        <span className='field-label'>
          Imported from {origin.sources.length}{' '}
          {origin.sources.length === 1 ? 'document' : 'documents'} (
          {IMPORT_MODE_LABELS[origin.mode].toLowerCase()})
        </span>
      </div>
      <ul className='handbook-origin-sources'>
        {origin.sources.map((s) => (
          <li key={s.documentId}>
            <span>{s.title}</span>
            <span className='handbook-meta'>
              {' · '}
              {s.passageIds.length}{' '}
              {s.passageIds.length === 1 ? 'passage' : 'passages'}
            </span>
          </li>
        ))}
      </ul>
      {fidelity &&
        (fidelity.unsupportedClaims.length > 0 ||
          fidelity.unusedPassageIds.length > 0) && (
          <div className='handbook-fidelity'>
            {fidelity.unsupportedClaims.length === 0 &&
            fidelity.unusedPassageIds.length === 0 ? null : (
              <>
                {fidelity.unsupportedClaims.length > 0 && (
                  <div>
                    <span className='handbook-meta'>
                      Not found in the sources (
                      {fidelity.unsupportedClaims.length}):
                    </span>
                    <ul className='handbook-fidelity-list'>
                      {fidelity.unsupportedClaims.map((c, index) => (
                        <li key={index}>{c}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {fidelity.unusedPassageIds.length > 0 && (
                  <span className='handbook-meta'>
                    Assigned passages not used (
                    {fidelity.unusedPassageIds.length}
                    ): {fidelity.unusedPassageIds.join(', ')}
                  </span>
                )}
              </>
            )}
          </div>
        )}
    </div>
  );
};

// ── Edit ────────────────────────────────────────────────────────────────

interface HandbookPageEditorProps extends DocumentPullProps {
  page: HandbookPage;
  parentOptions: FlatHandbookNode[];
  busyKey: string | null;
  onSaveDraft: (patch: UpdateHandbookDraftRequest) => void;
  onPublish: () => void;
  onDiscardDraft: () => void;
  onArchive: () => void;
}

/**
 * One handbook page: its draft (editable) and, when published, its live
 * version (read-only). With both, the draft shows first. Editing a page that
 * only has a live version creates the draft on the first save.
 */
export const HandbookPageEditor: FC<HandbookPageEditorProps> = ({
  page,
  parentOptions,
  busyKey,
  onSaveDraft,
  onPublish,
  onDiscardDraft,
  onArchive,
  loadDocuments,
  pullContent,
}) => {
  const { live, draft } = page;
  const [pullOpen, setPullOpen] = useState(false);
  const [pulledFrom, setPulledFrom] = useState<string | null>(null);
  const [view, setView] = useState<'draft' | 'live'>(draft ? 'draft' : 'live');
  const source = view === 'live' && live ? live : (draft ?? live);
  const initial = useMemo(
    () => (source ? fromVersion(source) : EMPTY_FORM),
    [source],
  );
  const [values, setValues] = useState<FormValues>(initial);

  const busy = busyKey !== null;
  const readOnly = view === 'live' && !!draft;
  const dirty = !sameForm(values, initial);
  const update = (patch: Partial<FormValues>) =>
    setValues((v) => ({ ...v, ...patch }));

  const switchView = (next: 'draft' | 'live') => {
    const nextSource = next === 'live' ? live : draft;
    setView(next);
    setValues(nextSource ? fromVersion(nextSource) : EMPTY_FORM);
  };

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    const title = values.title.trim();
    if (!title) return;
    onSaveDraft({
      title,
      description: values.description.trim() || null,
      parentSlug: values.parentSlug || null,
      bodyMarkdown: values.bodyMarkdown,
    });
  };

  const showingDraft = view === 'draft' && !!draft;
  const canPull = !readOnly && !!loadDocuments && !!pullContent;

  return (
    <div className='handbook-editor' data-testid='handbook-editor'>
      <form className='organisation-card' onSubmit={handleSave}>
        <div className='handbook-editor-header'>
          <div className='handbook-editor-title'>
            <h3 className='organisation-card-title'>{source?.title}</h3>
          </div>
          {live && draft ? (
            <div
              className='handbook-segmented'
              role='group'
              aria-label='Version shown'
            >
              <button
                type='button'
                className={`handbook-segment${showingDraft ? ' active' : ''}`}
                aria-pressed={showingDraft}
                disabled={busy}
                onClick={() => switchView('draft')}
              >
                Draft
              </button>
              <button
                type='button'
                className={`handbook-segment${showingDraft ? '' : ' active'}`}
                aria-pressed={!showingDraft}
                disabled={busy}
                onClick={() => switchView('live')}
              >
                Live
              </button>
            </div>
          ) : draft ? (
            <span className='handbook-badge draft'>Draft · not published</span>
          ) : (
            <span className='handbook-badge live'>Live</span>
          )}
        </div>

        {source?.origin && <HandbookOriginBlock origin={source.origin} />}

        <label className='organisation-field'>
          <span className='field-label'>Title</span>
          <input
            type='text'
            className='field-input'
            value={values.title}
            maxLength={200}
            disabled={busy}
            readOnly={readOnly}
            onChange={(e) => update({ title: e.target.value })}
          />
        </label>
        <label className='organisation-field'>
          <span className='field-label'>Description (optional)</span>
          <input
            type='text'
            className='field-input'
            value={values.description}
            maxLength={500}
            disabled={busy}
            readOnly={readOnly}
            placeholder='One line on what this page covers.'
            onChange={(e) => update({ description: e.target.value })}
          />
        </label>
        <ParentSelect
          value={values.parentSlug}
          options={parentOptions}
          disabled={busy || readOnly}
          onChange={(parentSlug) => update({ parentSlug })}
        />
        {canPull && !pullOpen && (
          <div className='handbook-pull-prompt'>
            <span>
              {values.bodyMarkdown.trim()
                ? 'Replace this page’s text with content from a document.'
                : 'This page is empty.'}
            </span>
            <button
              type='button'
              className='handbook-pill small'
              disabled={busy}
              onClick={() => setPullOpen(true)}
            >
              Pull content from a document
            </button>
          </div>
        )}
        {pullOpen && loadDocuments && pullContent ? (
          <>
            <HandbookDocumentPull
              title={values.title}
              disabled={busy}
              loadDocuments={loadDocuments}
              pull={pullContent}
              onPulled={(fill) => {
                update(pulledValues(values, fill));
                setPulledFrom(fillSourceTitle(fill));
                setPullOpen(false);
              }}
            />
            <button
              type='button'
              className='handbook-setup-link handbook-pull-cancel'
              onClick={() => setPullOpen(false)}
            >
              Write it myself instead
            </button>
          </>
        ) : (
          <>
            {pulledFrom && (
              <p className='handbook-pulled'>
                Written from {pulledFrom}. Check it, then save the draft.
              </p>
            )}
            <BodyField
              key={pulledFrom ?? 'body'}
              value={values.bodyMarkdown}
              disabled={busy}
              readOnly={readOnly}
              onChange={(bodyMarkdown) => update({ bodyMarkdown })}
            />
          </>
        )}

        <div className='handbook-editor-actions'>
          <div className='handbook-editor-actions-start'>
            {draft && (
              <button
                type='button'
                className='handbook-pill small'
                disabled={busy}
                onClick={onDiscardDraft}
              >
                Discard draft
              </button>
            )}
            {live && (
              <button
                type='button'
                className='handbook-pill small danger'
                disabled={busy}
                onClick={onArchive}
              >
                Archive
              </button>
            )}
          </div>
          <div className='handbook-editor-actions-end'>
            {!readOnly && (
              <button
                type='submit'
                className={`handbook-pill small${draft && !dirty ? '' : ' primary'}`}
                disabled={busy || !dirty || !values.title.trim()}
              >
                {busyKey === 'save' ? 'Saving…' : 'Save draft'}
              </button>
            )}
            {draft && (
              <button
                type='button'
                className={`handbook-pill small${dirty ? '' : ' primary'}`}
                disabled={busy || dirty}
                title={dirty ? 'Save the draft before publishing' : undefined}
                onClick={onPublish}
              >
                {busyKey === 'publish' ? 'Publishing…' : 'Publish'}
              </button>
            )}
          </div>
        </div>
        {dirty && !readOnly && (
          <span className='organisation-field-hint'>
            Unsaved changes. Save the draft before publishing.
          </span>
        )}
      </form>
    </div>
  );
};

export default HandbookPageEditor;
